// Tableau : Kanban en saison 1, backlog + sprints Scrum ensuite (R-TAB-01 à R-TAB-06).
import { useMemo, useState, type DragEvent } from 'react';
import { useMagasin, magasin } from '../donnees/magasin';
import type { Colonne, ElementTableau, Sprint } from '@partage/types';
import { ajouterJours, jourPedagogique } from '@partage/dates';

const COLONNES: { id: Colonne; titre: string }[] = [{ id: 'a_faire', titre: 'À faire' }, { id: 'en_cours', titre: 'En cours' }, { id: 'fait', titre: 'Fait' }];

function Element({ e, sprintActif }: { e: ElementTableau; sprintActif?: Sprint }) {
  const { etat } = useMagasin();
  const [ouvert, setOuvert] = useState(false);
  const deplacer = (sens: -1 | 1) => {
    const i = COLONNES.findIndex((c) => c.id === e.colonne) + sens;
    const cible = COLONNES[i];
    if (cible) void magasin.emettre('element_deplace', { element: e.id, colonne: cible.id, position: 0 });
  };
  const engage = sprintActif?.elements.includes(e.id);
  return (
    <div className={`element source-${e.source}`} draggable onDragStart={(ev: DragEvent) => ev.dataTransfer.setData('text/plain', e.id)}>
      <div style={{ fontWeight: 600 }}>{e.titre}</div>
      {e.session ? <span className="badge accent">{e.session}</span> : null}{e.domaine ? <span className="badge">{e.domaine}</span> : null}{e.sprint ? <span className="badge ok">sprint</span> : null}
      {ouvert ? (
        <div className="petit-texte" style={{ marginTop: '0.4em' }}>
          {e.description ? <p>{e.description}</p> : null}
          {e.story ? <p><em>En tant que</em> {e.story.en_tant_que}, <em>je veux</em> {e.story.je_veux}, <em>afin de</em> {e.story.afin_de}.</p> : null}
          {e.criteres?.map((c, i) => (
            <label key={i} style={{ fontWeight: 400 }}><input type="checkbox" checked={c.fait} onChange={() => void magasin.emettre('element_modifie', { element: e.id, criteres: e.criteres!.map((x, j) => (j === i ? { ...x, fait: !x.fait } : x)) })} />{c.texte}</label>
          ))}
        </div>
      ) : null}
      <div className="actions">
        <button className="petit" onClick={() => deplacer(-1)} disabled={e.colonne === 'a_faire'} aria-label="Colonne précédente">◀</button>
        <button className="petit" onClick={() => deplacer(1)} disabled={e.colonne === 'fait'} aria-label="Colonne suivante">▶</button>
        <button className="discret petit" onClick={() => setOuvert((o) => !o)}>{ouvert ? 'Réduire' : 'Détails'}</button>
        {etat.tableau.mode === 'scrum' && sprintActif ? <button className="discret petit" onClick={() => void magasin.emettre('element_engage', { sprint: sprintActif.id, element: e.id, engage: !engage })}>{engage ? 'Retirer du sprint' : 'Engager'}</button> : null}
        {e.source === 'manuel' ? <button className="discret petit" onClick={() => { if (window.confirm('Supprimer cet élément ?')) void magasin.emettre('element_supprime', { element: e.id }); }}>Supprimer</button> : null}
      </div>
    </div>
  );
}

export function Tableau() {
  const { etat } = useMagasin();
  const [titre, setTitre] = useState('');
  const [story, setStory] = useState({ en_tant_que: '', je_veux: '', afin_de: '' });
  const [criteres, setCriteres] = useState('');
  const [objectif, setObjectif] = useState('');
  const scrum = etat.tableau.mode === 'scrum';
  const sprintActif = etat.tableau.sprints.find((s) => s.statut === 'en_cours');
  const elements = useMemo(() => Object.values(etat.tableau.elements), [etat.tableau.elements]);
  const visibles = scrum && sprintActif ? elements.filter((e) => sprintActif.elements.includes(e.id) || e.colonne !== 'a_faire') : elements;
  const enCours = elements.filter((e) => e.colonne === 'en_cours').length;

  async function creer() {
    if (!titre.trim()) return;
    const s = story.en_tant_que && story.je_veux && story.afin_de ? story : undefined;
    await magasin.emettre('element_cree', { element: `manuel-${crypto.randomUUID().slice(0, 8)}`, titre: titre.trim(), colonne: 'a_faire', source: 'manuel', story: s, criteres: criteres.split('\n').map((c) => c.trim()).filter(Boolean) });
    setTitre(''); setStory({ en_tant_que: '', je_veux: '', afin_de: '' }); setCriteres('');
  }

  async function deposer(colonne: Colonne, ev: DragEvent) {
    ev.preventDefault();
    const id = ev.dataTransfer.getData('text/plain');
    if (id) await magasin.emettre('element_deplace', { element: id, colonne, position: 0 });
  }

  async function creerSprint() {
    const numero = etat.tableau.sprints.length + 1;
    const debut = jourPedagogique(new Date());
    const id = `sprint-${numero}`;
    await magasin.emettre('sprint_cree', { sprint: id, numero, objectif: objectif.trim() || `Sprint ${numero}`, debut, fin: ajouterJours(debut, 13) });
    await magasin.emettre('sprint_demarre', { sprint: id });
    setObjectif('');
  }

  return (
    <>
      <div className="ligne" style={{ marginBottom: '1em' }}>
        <h2 style={{ margin: 0 }}>{scrum ? 'Backlog et sprints' : 'Kanban'}</h2>
        {enCours > 2 ? <span className="badge attention">{enCours} éléments en cours : le Kanban conseille d’en finir un avant d’en commencer un autre</span> : null}
      </div>
      {scrum ? (
        <div className="carte" style={{ marginBottom: '1em' }}>
          {sprintActif ? (
            <div className="ligne">
              <strong>Sprint {sprintActif.numero}</strong> · {sprintActif.objectif} · du {sprintActif.debut} au {sprintActif.fin} · {sprintActif.elements.length} élément(s) engagé(s) (capacité indicative : 6 sessions)
              <span className="espace" />
              <button className="petit" onClick={() => { if (window.confirm('Terminer ce sprint ? Pense à écrire la revue et la rétro dans le journal.')) void magasin.emettre('sprint_termine', { sprint: sprintActif.id }); }}>Terminer le sprint</button>
            </div>
          ) : (
            <div className="ligne">
              <input placeholder="Objectif du sprint (2 semaines)" value={objectif} onChange={(e) => setObjectif(e.target.value)} style={{ maxWidth: 420 }} />
              <button className="principal" onClick={() => void creerSprint()}>Démarrer le sprint {etat.tableau.sprints.length + 1}</button>
            </div>
          )}
        </div>
      ) : null}
      <div className="kanban">
        {COLONNES.map((col) => {
          const liste = visibles.filter((e) => e.colonne === col.id).sort((a, b) => a.position - b.position);
          return (
            <div className="colonne" key={col.id} onDragOver={(e) => e.preventDefault()} onDrop={(e) => void deposer(col.id, e)}>
              <h3>{col.titre} <span className="badge">{liste.length}</span></h3>
              {liste.map((e) => <Element e={e} key={e.id} sprintActif={sprintActif} />)}
            </div>
          );
        })}
      </div>
      <div className="carte" style={{ marginTop: '1em' }}>
        <h3>Ajouter un élément</h3>
        <div className="champ"><label htmlFor="titre">Titre</label><input id="titre" value={titre} onChange={(e) => setTitre(e.target.value)} /></div>
        {scrum ? (
          <div className="grille">
            <div className="champ"><label>En tant que…</label><input value={story.en_tant_que} onChange={(e) => setStory({ ...story, en_tant_que: e.target.value })} /></div>
            <div className="champ"><label>je veux…</label><input value={story.je_veux} onChange={(e) => setStory({ ...story, je_veux: e.target.value })} /></div>
            <div className="champ"><label>afin de…</label><input value={story.afin_de} onChange={(e) => setStory({ ...story, afin_de: e.target.value })} /></div>
          </div>
        ) : null}
        <div className="champ"><label htmlFor="criteres">Critères d’acceptation (un par ligne)</label><textarea id="criteres" rows={2} value={criteres} onChange={(e) => setCriteres(e.target.value)} /></div>
        <button className="principal" onClick={() => void creer()}>Ajouter au backlog</button>
      </div>
    </>
  );
}
